import { useDispatch } from 'react-redux';
import React from 'react';
import { useAsync } from 'react-use';
import { Skeleton } from '@material-ui/lab';
import { Box, Button, Collapse } from '@material-ui/core';

import { getCustomers } from 'services/ErplyAPI';
import { updateProductOrder } from 'actions/ShoppingCart';
import { PosPlugin } from 'plugins/plugin';
import CustomerInput from 'components/inputs/CustomerInput/CustomerInput';

type Order = Parameters<Required<PosPlugin>['UIProductFormBody']>[0]['order'];

/**
 * Allows selecting and deselecting a customer to assign to this order
 *
 * The customer will be saved to / loaded from the 'customerID' prop, which in
 * turn is synced to JSON API 'inv_rows' BrazilPOS.customerID
 *
 * <pre>
 * Assigned customer: None
 * ┌───────────────┐
 * │Assign customer│
 * └───────────────┘
 * ----------------------------------------------------------------------------
 * Assigned customer: None
 * ┌───────────────┐
 * │Assign customer│
 * └───────────────┘
 * ┌───────────────────────────────────────────┐
 * │ Dog|                                    v │
 * ├─────────────────────────────────────────┬─┘
 * ├─────────────────────────────────────────┤
 * │ Coffee, Dog                             │
 * ├─────────────────────────────────────────┤
 * │ Clementine, Dog                         │
 * ├─────────────────────────────────────────┤
 * │ Maple, Dog                              │
 * ├─────────────────────────────────────────┤
 * └─────────────────────────────────────────┘
 * ----------------------------------------------------------------------------
 * Assigned customer: Blackberry Deer
 * ┌───────────────┐ ┌─────────────────────────┐
 * │Assign customer│ │Remove current assignment│
 * └───────────────┘ └─────────────────────────┘
 */
export function CustomerSection({ order }: { order: Order }) {
  const dispatch = useDispatch();
  const id = order?.customerID;
  const [open, setOpen] = React.useState(false);

  const customerName = useAsync(async () => {
    if (!id) return undefined;
    const [customer] = await getCustomers({
      customerID: id,
      getFields: 'customerID,companyName,firstName,lastName',
    });
    return customer.companyName || `${customer.firstName} ${customer.lastName}`;
  }, [id]);

  function setCustomer(customerID: number | null) {
    return (dispatch(
      updateProductOrder({ orderIndex: order.orderIndex, customerID }),
      // Yes, we have useAppDispatch, but importing that causes the build to fail
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) as any) as Promise<void>;
  }

  const jsxName = id
    ? customerName.value ?? (
        <Skeleton style={{ display: 'inline-block', width: '12ch' }} />
      )
    : 'None';

  return (
    <div className="customer-assignment">
      <h3>Assigned customer: {jsxName}</h3>

      <Box
        display="flex"
        justifyContent="flex-start"
        margin={2}
        style={{ gap: 8 }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpen(true)}
        >
          Assign customer
        </Button>
        {id && (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setCustomer(null)}
          >
            Remove customer assignment
          </Button>
        )}
      </Box>
      <Collapse in={open} mountOnEnter unmountOnExit>
        <CustomerInput
          value={null}
          setValue={async c => {
            await setCustomer(c?.customerID ?? null);
            setOpen(false);
          }}
        />
      </Collapse>
    </div>
  );
}
